// EventSource wrapper for server.js SSE streams:
//   - /api/command?cmd=... (ephemeral, dies on disconnect unless long-running)
//   - /api/jobs/:id/stream (persistent, replay + tail)
//
// server.js emits lines like:
//   data: {"type":"progress","data":{"phase":"fetching","pct":42}}
//   data: {"type":"log","data":{"line":"..."}}
//   data: {"type":"token","data":{"token":"..."}}
//   data: {"type":"result","data":{...}}
//   data: {"type":"error","data":{"message":"..."}}
//   data: {"type":"done"}

import { useEffect, useRef, useState } from 'react';
import type { SSEEvent, SSEEventType } from './types';

export interface UseSSEOptions {
  /** If false, do not open the stream (lets you gate on a ready state). */
  enabled?: boolean;
  onEvent?: (e: SSEEvent) => void;
  onError?: (err: Event) => void;
}

export interface UseSSEState {
  events: SSEEvent[];
  status: 'idle' | 'connecting' | 'streaming' | 'done' | 'error';
  tokens: string;             // accumulated LLM tokens for convenience
  result: unknown | null;
  error: string | null;
  close: () => void;
}

export function useSSE(url: string | null, opts: UseSSEOptions = {}): UseSSEState {
  const { enabled = true, onEvent, onError } = opts;
  const [events, setEvents] = useState<SSEEvent[]>([]);
  const [status, setStatus] = useState<UseSSEState['status']>('idle');
  const [tokens, setTokens] = useState('');
  const [result, setResult] = useState<unknown>(null);
  const [error, setError] = useState<string | null>(null);
  const sourceRef = useRef<EventSource | null>(null);

  useEffect(() => {
    if (!enabled || !url) return;
    setStatus('connecting');
    setEvents([]);
    setTokens('');
    setResult(null);
    setError(null);

    const es = new EventSource(url);
    sourceRef.current = es;

    es.onopen = () => setStatus('streaming');

    es.onmessage = (msg) => {
      let raw: unknown;
      try {
        raw = JSON.parse(msg.data);
      } catch {
        raw = { type: 'log', data: { line: msg.data } };
      }
      // Normalize legacy server shapes ({type:"stdout"|"stderr", text:"..."})
      // into our canonical SSEEvent shape.
      const evt: SSEEvent = normalizeSSEEvent(raw);
      setEvents((prev) => [...prev, evt]);
      onEvent?.(evt);

      if (evt.type === 'token' && evt.data && typeof (evt.data as { token?: string }).token === 'string') {
        setTokens((prev) => prev + (evt.data as { token: string }).token);
      } else if (evt.type === 'log' && evt.data && typeof (evt.data as { line?: string }).line === 'string') {
        // Also accumulate log lines into tokens so UIs get free-form output
        setTokens((prev) => prev + (evt.data as { line: string }).line);
      } else if (evt.type === 'result') {
        setResult(evt.data);
      } else if (evt.type === 'error') {
        const m = (evt.data as { message?: string } | undefined)?.message || 'stream error';
        setError(m);
        setStatus('error');
      } else if (evt.type === 'done') {
        setStatus('done');
        es.close();
      }
    };

    es.onerror = (err) => {
      onError?.(err);
      // EventSource auto-reconnects; only mark error if it's terminal.
      if (es.readyState === EventSource.CLOSED) {
        setError((prev) => prev || 'connection lost');
        setStatus('error');
      }
    };

    return () => {
      es.close();
      sourceRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, enabled]);

  const close = () => {
    sourceRef.current?.close();
    setStatus('done');
  };

  return { events, status, tokens, result, error, close };
}

/**
 * Map various server-side SSE shapes to our canonical { type, data } form.
 *   server.js /api/command emits { type: "stdout", text: "..." } per line.
 *   /api/jobs/:id/stream emits  { type: "log" | "result" | ... } already.
 */
function normalizeSSEEvent(raw: unknown): SSEEvent {
  if (raw && typeof raw === 'object') {
    const obj = raw as { type?: string; data?: unknown; text?: string; line?: string; message?: string; code?: number };
    const t = obj.type;
    if (t === 'stdout' || t === 'stderr') {
      return { type: 'log', data: { line: obj.text || '' } };
    }
    // server.js emits `{type:"exit", code:N}` when the child process closes.
    // Treat it as a `done` event so ES closes — and crucially, do NOT
    // accumulate it into the tokens buffer (which would render the literal
    // `{"type":"exit","code":0}` string at the end of the user's markdown).
    if (t === 'exit') {
      return { type: 'done', data: { code: obj.code } };
    }
    if (t === 'token' || t === 'log' || t === 'result' || t === 'error' || t === 'done' || t === 'progress') {
      // Already canonical or close enough
      return { type: t as SSEEventType, data: obj.data ?? { line: obj.line, message: obj.message, text: obj.text } };
    }
    // Unknown structured object — treat as a no-op done so its JSON form
    // never leaks into rendered markdown. Old behaviour stuffed
    // JSON.stringify(obj) into a log line and corrupted UIs.
    return { type: 'done', data: undefined };
  }
  return { type: 'log', data: { line: String(raw) } };
}

/** Helper to build the /api/command URL with typed payload. */
export function commandUrl(cmd: string, options?: Record<string, unknown>, payload?: string): string {
  const u = new URL('/api/command', window.location.origin);
  u.searchParams.set('cmd', cmd);
  if (options) u.searchParams.set('options', JSON.stringify(options));
  if (payload) u.searchParams.set('payload', payload);
  return u.pathname + u.search;
}
